import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Badge, Flex, Text, Box, HStack, Select, Input,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import { FiFileText } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { recorridosApi, Recorrido, abrirPdfEnNuevaPestana } from '../../../api/serviciosApi';
import FormularioRecorrido, { ESTATUS_RECORRIDO, estatusRecorridoColor } from './FormularioRecorrido';
import ModalRecorridoDetalle from './ModalRecorridoDetalle';

const TablaRecorridos: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);
  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenDetalle, onOpen: onOpenDetalle, onClose: onCloseDetalle } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();
  const { isOpen: isOpenCerrar, onOpen: onOpenCerrar, onClose: onCloseCerrar } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [data, setData] = useState<Recorrido[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try { setData(await recorridosApi.list()); } catch { setData([]); } finally { setCargando(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const matchBusqueda = busqueda
      ? item.folio?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.operador?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.camion?.numeroEconomico?.toLowerCase().includes(busqueda.toLowerCase())
      : true;
    return matchEstatus && matchBusqueda;
  });
  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleVer = async (item: any) => { try { setSelectedItem(await recorridosApi.get(item.id)); } catch { setSelectedItem(item); } onOpenVer(); };
  const handleEditar = async (item: any) => { try { setSelectedItem(await recorridosApi.get(item.id)); } catch { setSelectedItem(item); } onOpenEditar(); };
  const handleDetalle = (item: any) => { setDetalleId(item.id); onOpenDetalle(); };
  const handleEliminarClick = (item: any) => { setSelectedItem(item); onOpenEliminar(); };
  const handleCerrarClick = (item: any) => { setSelectedItem(item); onOpenCerrar(); };
  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };

  const handleEliminarConfirmar = async () => {
    if (!selectedItem) return;
    setProcesando(true);
    try { await recorridosApi.remove(selectedItem.id); toast({ title: 'Recorrido eliminado', status: 'success', duration: 3000 }); onCloseEliminar(); fetchData(); }
    catch (error: any) { toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 }); }
    finally { setProcesando(false); }
  };
  const handleCerrarConfirmar = async () => {
    if (!selectedItem) return;
    setProcesando(true);
    try { await recorridosApi.cerrar(selectedItem.id); toast({ title: 'Recorrido finalizado', status: 'success', duration: 3000 }); onCloseCerrar(); fetchData(); }
    catch (error: any) { toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo cerrar.', status: 'error', duration: 4000 }); }
    finally { setProcesando(false); }
  };

  const fmt = (v?: string) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');
  const handleExportExcel = () => {
    const filas = datosFiltrados.map((i) => ({
      'Folio': i.folio, 'Fecha': fmt(i.fecha), 'Camión': i.camion?.numeroEconomico, 'Ruta': i.ruta?.nombre,
      'Operador': i.operador, 'Localidad': i.localidad, 'Estatus': i.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Recorridos');
    XLSX.writeFile(wb, `Recorridos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const [generandoPdf, setGenerandoPdf] = useState(false);
  const handleReportePdf = async () => {
    setGenerandoPdf(true);
    try {
      const params: any = {};
      if (filtroEstatus) params.estatus = filtroEstatus;
      const blob = await recorridosApi.reportePdf(params);
      abrirPdfEnNuevaPestana(blob);
    } catch {
      toast({ title: 'Error', description: 'No se pudo generar el reporte PDF.', status: 'error', duration: 4000 });
    } finally {
      setGenerandoPdf(false);
    }
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por folio, operador o camión..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '250px' }} />
          <Select placeholder="Filtrar por estatus" value={filtroEstatus}
            onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="170px">
            {ESTATUS_RECORRIDO.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<FiFileText />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleReportePdf} isLoading={generandoPdf}>Reporte PDF</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>Nuevo recorrido</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Folio</Th>
              <Th color="white" fontSize="xs">Fecha</Th>
              <Th color="white" fontSize="xs">Camión</Th>
              <Th color="white" fontSize="xs">Ruta</Th>
              <Th color="white" fontSize="xs">Operador</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.400">No se encontraron recorridos</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td fontWeight="bold">{item.folio}</Td>
                  <Td>{fmt(item.fecha)}</Td>
                  <Td>{item.camion?.numeroEconomico || '—'}</Td>
                  <Td>{item.ruta?.nombre || '—'}</Td>
                  <Td>{item.operador || '—'}</Td>
                  <Td><Badge colorScheme={estatusRecorridoColor(item.estatus)} fontSize="xs" px={2} py={0.5} borderRadius="full">{item.estatus}</Badge></Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => handleEditar(item)}>Editar</Button>
                      <Button size="xs" colorScheme="teal" variant="outline" onClick={() => handleDetalle(item)}>Detalle</Button>
                      {item.estatus !== 'Finalizado' && item.estatus !== 'Cancelado' && (
                        <Button size="xs" colorScheme="green" variant="solid" onClick={() => handleCerrarClick(item)}>Cerrar</Button>
                      )}
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleEliminarClick(item)}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return <Button key={page} size="xs" bg={currentPage === page ? '#5b182b' : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>;
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="3xl" scrollBehavior="inside">
        <ModalOverlay /><ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Detalle del Recorrido</ModalHeader>
          <ModalBody py={5}>{selectedItem && <FormularioRecorrido modo="view" data={selectedItem} />}</ModalBody>
          <ModalFooter><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="3xl" scrollBehavior="inside">
        <ModalOverlay /><ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Editar Recorrido</ModalHeader>
          <ModalBody py={5}>{selectedItem && <FormularioRecorrido modo="edit" data={selectedItem} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}</ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="3xl" scrollBehavior="inside">
        <ModalOverlay /><ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Nuevo Recorrido</ModalHeader>
          <ModalBody py={5}><FormularioRecorrido modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} /></ModalBody>
        </ModalContent>
      </Modal>

      <ModalRecorridoDetalle isOpen={isOpenDetalle} onClose={onCloseDetalle} recorridoId={detalleId} onCambios={fetchData} />

      <AlertDialog isOpen={isOpenCerrar} leastDestructiveRef={cancelRef} onClose={onCloseCerrar}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">Cerrar recorrido</AlertDialogHeader>
          <AlertDialogBody>¿Finalizar el recorrido <strong>{selectedItem?.folio}</strong>? El estatus cambiará a <strong>Finalizado</strong>.</AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onCloseCerrar} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="green" onClick={handleCerrarConfirmar} ml={3} isLoading={procesando} loadingText="Cerrando...">Confirmar</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">Eliminar recorrido</AlertDialogHeader>
          <AlertDialogBody>¿Eliminar el recorrido <strong>{selectedItem?.folio}</strong>? Esta acción no se puede deshacer.</AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleEliminarConfirmar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaRecorridos;
